import React from 'react';
import classNames from 'classnames';
import styles from './QuotationBlocks.module.scss';

import { Field, ImageField, NextImage, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type FieldsItem = {
  id: string;
  fields: {
    image?: ImageField;
    quotation?: Field<string>;
    authorName?: Field<string>;
    authorTitle?: Field<string>;
  };
};

type Fields = {
  items: FieldsItem[];
  title?: Field<string>;
};

interface Rendering {
  uid: string;
  params?: { background?: string };
}

interface QuotationBlocksProps {
  fields: Fields;
  rendering: Rendering;
}

const QuotationBlocks: React.FC<QuotationBlocksProps> = ({ fields, rendering }) => {
  const { sitecoreContext: context } = useSitecoreContext();
  const isExperienceEditor = context.pageEditing;
  const haveTitleValue = !!fields?.title?.value;
  const isImageProvided = !!fields?.items?.[0]?.fields?.image?.value?.src;
  const backgroundClass = rendering?.params?.background ?? 'background-digital-deep-grey';

  return (
    <section
      id={rendering.uid}
      data-e2e-component-uid={rendering.uid}
      className={classNames(styles.quotationBlocks, 'default-section-padding', {
        [backgroundClass]: fields.items.length === 1,
      })}
    >
      <div className="container w-100">
        {fields.items.length === 1 ? (
          fields.items.map((quoteBlock) => (
            <div
              key={quoteBlock.id}
              className={classNames(
                'd-flex flex-column-reverse flex-md-row row',
                isImageProvided && 'justify-content-xl-center'
              )}
            >
              <div
                className={classNames(
                  'd-flex justify-content-start justify-content-md-end align-items-start col-12 mt-3 mt-md-0',
                  {
                    'justify-content-xl-center': isImageProvided,
                    'col-md-0 col-lg-1': !isImageProvided && !isExperienceEditor,
                    'col-md-2': isImageProvided || isExperienceEditor,
                  }
                )}
              >
                {(isExperienceEditor || isImageProvided) && (
                  <NextImage
                    field={quoteBlock.fields.image}
                    sizes="
                        (max-width: 575px) 20vw,
                        (max-width: 767px) 95px,
                        (max-width: 991px) 90px,
                        (max-width: 1199px) 15vw,
                        170px
                    "
                  />
                )}
                <div className="d-flex flex-column d-md-none">
                  {(isExperienceEditor || quoteBlock.fields.authorName?.value) && (
                    <Text
                      field={quoteBlock.fields.authorName}
                      tag="p"
                      className={`background-to-change footnote ${styles.footnote}`}
                    />
                  )}
                  {(isExperienceEditor || quoteBlock.fields.authorTitle?.value) && (
                    <Text
                      field={quoteBlock.fields.authorTitle}
                      tag="p"
                      className="background-to-change caption"
                    />
                  )}
                </div>
              </div>
              <div className="d-flex flex-column col-md-10 col-xl-8">
                {(isExperienceEditor || quoteBlock.fields.quotation?.value) && (
                  <Text
                    field={quoteBlock.fields.quotation}
                    tag="blockquote"
                    className={classNames('background-to-change h3', styles.h3)}
                  />
                )}
                {(isExperienceEditor || quoteBlock.fields.authorName?.value) && (
                  <Text
                    field={quoteBlock.fields.authorName}
                    tag="p"
                    className={`d-none d-md-flex mt-3 background-to-change footnote ${styles.footnote}`}
                  />
                )}
                {(isExperienceEditor || quoteBlock.fields.authorTitle?.value) && (
                  <Text
                    field={quoteBlock.fields.authorTitle}
                    tag="p"
                    className="d-none d-md-flex background-to-change caption"
                  />
                )}
              </div>
            </div>
          ))
        ) : (
          <>
            <div className="row">
              <div
                className={classNames(
                  'col col-xl-8 col-md-10',
                  fields.items.length === 1 && 'col-xl-8 col-md-10'
                )}
              >
                {(isExperienceEditor || haveTitleValue) && (
                  <Text tag="h3" className="mb-3" field={fields.title} />
                )}
              </div>
            </div>
            <div className="row d-flex">
              {fields.items.map((quoteBlock) => (
                <div key={quoteBlock.id} className="col-12 col-md mt-5 mt-md-0">
                  {(isExperienceEditor || quoteBlock.fields.quotation?.value) && (
                    <Text
                      tag="blockquote"
                      className="body-default text-grey"
                      field={quoteBlock.fields.quotation}
                    />
                  )}
                  <Text
                    tag="p"
                    className={`mt-2 background-to-change footnote ${styles.footnote}`}
                    field={quoteBlock.fields.authorName}
                  />
                  <Text
                    tag="p"
                    className="background-to-change caption text-grey"
                    field={quoteBlock.fields.authorTitle}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default QuotationBlocks;
